import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Check, Keyboard, Loader2, Scissors, ShoppingBag, Store, Ticket, X } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { PageIntro } from "@/components/layout/PageIntro";
import api, { formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { GhostButton, StoreButton } from "@/components/Buttons";
import { useAuth } from "@/context/AuthContext";
import { useShop } from "@/lib/useShop";
import { themeOf } from "@/lib/mall";
import CouponCard from "@/components/mall/CouponCard";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

/** One Gang tier on white: price, the DTF math spelled out, the perks, one button. */
function TierCard({ tier, mine, onJoin, busy, loggedIn, yearly, theme, top }) {
    const price = yearly ? tier.price_year : tier.price_month;
    return (
        <div className={`relative flex h-full flex-col rounded-3xl border bg-white p-6 transition-colors ${mine ? "border-zinc-900 ring-2 ring-zinc-900" : top ? "border-zinc-900" : "border-zinc-200 hover:border-zinc-400"}`}
            data-testid={`tier-card-${tier.id}`}>
            {(mine || top) && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest"
                    style={{ background: theme.primary, color: theme.on || "#fff" }} data-testid={`tier-flag-${tier.id}`}>
                    {mine ? <><BadgeCheck size={12} /> Your tier</> : "Most shops pick this"}
                </span>
            )}
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">{tier.name}</p>
            <p className="mt-3 font-display text-4xl font-black tracking-tight text-zinc-900">
                {money(price)}<span className="text-base font-bold text-zinc-500">/{yearly ? "yr" : "mo"}</span>
            </p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-zinc-600" data-testid={`tier-offer-${tier.id}`}>
                {yearly ? "58% off · 30 days free" : tier.first_month ? `$${tier.first_month} first month` : "30-day free trial"}
            </p>
            <p className="mt-4 text-sm text-zinc-700">{tier.headline}</p>
            <div className="mt-4 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3" data-testid={`tier-math-${tier.id}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">DTF gang sheets</p>
                <p className="mt-1 font-display text-2xl font-black text-zinc-900">
                    ${tier.dtf_per_ft}<span className="text-sm font-bold text-zinc-500">/ft</span>
                    <span className="ml-2 text-sm font-semibold text-zinc-400 line-through">${tier.dtf_base}/ft</span>
                </p>
                <p className="mt-0.5 text-xs text-zinc-600">{tier.dtf_pct}% off transfers · {tier.sitewide_pct}% off everything else</p>
            </div>
            <ul className="mt-4 flex-1 space-y-2">
                {tier.perks.map((p) => (
                    <li key={p} className="flex gap-2.5 text-sm text-zinc-700">
                        <Check size={16} className="mt-0.5 shrink-0" style={{ color: theme.ink || theme.primary }} /> {p}
                    </li>
                ))}
            </ul>
            <div className="mt-6">
                {mine ? (
                    <GhostButton className="w-full" disabled data-testid={`tier-current-${tier.id}`}>Active on your account</GhostButton>
                ) : loggedIn ? (
                    <StoreButton theme={theme} className="w-full" onClick={() => onJoin(tier)} disabled={busy} data-testid={`tier-join-${tier.id}`}>
                        {busy ? <Loader2 className="animate-spin" size={16} /> : "Start 30-day trial"}
                    </StoreButton>
                ) : (
                    <Link to="/login" state={{ from: "/wholesale" }} className="block" data-testid={`tier-login-${tier.id}`}>
                        <StoreButton theme={theme} className="w-full">Log in to join</StoreButton>
                    </Link>
                )}
            </div>
        </div>
    );
}

const STEPS = [
    [Scissors, "Clip a code", "Every coupon below is a code. Tap it and it copies."],
    [Keyboard, "Type it at checkout", "There is a coupon box on this shop's checkout page. Paste it there."],
    [ShoppingBag, "One coupon per order", "The money comes off that order. A coupon is only good at the shop that printed it."],
];

/** Wholesale: this shop's coupons, then the Gang — the monthly membership that discounts at every shop that honors it. */
export default function Wholesale() {
    const { user } = useAuth();
    const { shopSlug, store } = useShop();
    const [book, setBook] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [mem, setMem] = useState(null);
    const [busy, setBusy] = useState(false);
    const [yearly, setYearly] = useState(false);

    useEffect(() => {
        setBook(null);
        api.get("/coupons", { params: { store: shopSlug || undefined } }).then(({ data }) => setBook(data)).catch(() => setBook({ coupons: [] }));
    }, [shopSlug]);
    useEffect(() => { api.get("/membership/tiers").then(({ data }) => setTiers(data.tiers)).catch(() => { }); }, []);
    useEffect(() => {
        if (!user) { setMem(null); return; }
        api.get("/membership/me").then(({ data }) => setMem(data.membership)).catch(() => { });
    }, [user]);

    const theme = themeOf(store);
    const shop = book?.store || store;
    const demo = !shopSlug || book?.demo;
    const active = mem && ["trial", "active"].includes(mem.status) ? mem : null;
    const coupons = book?.coupons || [];
    const honors = store ? store.crew_perks !== false : true;

    const join = async (tier) => {
        setBusy(true);
        try {
            const { data } = await api.post("/payments/checkout", {
                lookup_key: yearly ? tier.yearly_key : tier.monthly_key,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const cancel = async () => {
        setBusy(true);
        try { const { data } = await api.post("/payments/subscription/cancel", { kind: "gang" }); setMem({ ...mem, status: "cancelled" }); toast.success(data.note); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <Layout>
            <PageIntro trail={[{ label: "Wholesale" }]} kicker={`Wholesale · ${shop?.name || "13thav"}`} title="Coupons & wholesale." testid="wholesale-intro"
                description="Every shop on 13thav runs its own promotions, so a coupon that is good here may not be good at the next shop. Below are the coupons this shop is running right now. Under them is the Gang — a monthly membership that takes a standing discount off at every shop that honors it."
                right={
                    <Link to={user ? "/account/coupons" : "/account/sell"} data-testid="print-your-own">
                        <GhostButton><Store size={15} /> Print your own coupons</GhostButton>
                    </Link>
                } />

            <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-testid="coupon-section">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Coupons</p>
                        <h2 className="mt-1 flex items-center gap-2.5 font-display text-3xl font-black tracking-tight text-zinc-900 sm:text-4xl" data-testid="coupon-heading">
                            <Ticket style={{ color: theme.ink || theme.primary }} /> {shop ? `${shop.name}'s coupons.` : "Coupons."}
                        </h2>
                        <p className="mt-2 text-base text-zinc-600" data-testid="coupon-book-count">
                            {coupons.length} live {coupons.length === 1 ? "coupon" : "coupons"}
                            {demo ? " · these are 13thav Demo's. Each shop prints its own — pick a shop in the directory to see theirs." : ` · good at ${shop?.name} only.`}
                        </p>
                    </div>
                    {demo && (
                        <Link to="/directory" data-testid="coupon-pick-shop">
                            <GhostButton><Store size={15} /> Pick a shop</GhostButton>
                        </Link>
                    )}
                </div>

                {!book ? (
                    <div className="flex min-h-[20vh] items-center justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>
                ) : coupons.length === 0 ? (
                    <div className="mt-6 rounded-3xl border border-dashed border-zinc-300 bg-white p-12 text-center" data-testid="coupon-book-empty">
                        <Ticket size={32} className="mx-auto text-zinc-400" />
                        <p className="mt-4 text-zinc-700">{shop?.name || "This shop"} has no coupons running right now. Check back — shops print new ones all the time.</p>
                    </div>
                ) : (
                    <div className="mt-6 grid gap-5 lg:grid-cols-2" data-testid="coupon-book">
                        {coupons.map((c, i) => (
                            <Reveal key={c.id} delay={i * 0.05}><CouponCard coupon={c} /></Reveal>
                        ))}
                    </div>
                )}

                <div className="mt-8 grid gap-3 sm:grid-cols-3" data-testid="coupon-steps">
                    {STEPS.map(([Icon, head, body], i) => (
                        <div key={head} className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-4">
                            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                                <Icon size={13} style={{ color: theme.ink || theme.primary }} /> Step {i + 1}
                            </p>
                            <p className="mt-1.5 font-display text-base font-black text-zinc-900">{head}</p>
                            <p className="mt-1 text-sm text-zinc-600">{body}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="border-y border-zinc-200 bg-zinc-50" data-testid="gang-section">
                <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <div className="max-w-2xl">
                            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">The Gang · wholesale membership</p>
                            <h2 className="mt-1 font-display text-3xl font-black tracking-tight text-zinc-900 sm:text-4xl">Join the Gang.</h2>
                            <p className="mt-2 text-base text-zinc-600" data-testid="gang-blurb">
                                A coupon is one order. The Gang is every order — wholesale DTF rates and a standing discount
                                that comes off automatically at checkout at every shop that honors it. Three tiers, 30 days free, cancel any time.
                            </p>
                            {shop && (
                                <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-3 py-1 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-700" data-testid="gang-honored">
                                    <BadgeCheck size={12} style={{ color: theme.ink || theme.primary }} />
                                    {honors ? `${shop.name} honors Gang prices` : `${shop.name} does not honor Gang prices yet`}
                                </p>
                            )}
                        </div>
                        <button onClick={() => setYearly(!yearly)} data-testid="gang-billing-toggle"
                            className="inline-flex self-start rounded-full border border-zinc-300 bg-white p-1 font-mono text-[11px] uppercase tracking-[0.16em]">
                            <span className={`rounded-full px-3 py-1.5 ${!yearly ? "bg-zinc-900 text-white" : "text-zinc-600"}`}>Monthly</span>
                            <span className={`rounded-full px-3 py-1.5 ${yearly ? "bg-zinc-900 text-white" : "text-zinc-600"}`}>Yearly · 58% off</span>
                        </button>
                    </div>

                    {active && (
                        <div className="mt-8 flex flex-col gap-3 rounded-3xl border border-zinc-900 bg-white p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="membership-status">
                            <div>
                                <p className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-900">
                                    <BadgeCheck size={13} /> {active.tier_name} member · {active.status}
                                </p>
                                <p className="mt-2 text-sm text-zinc-700">
                                    Perks are live on {user?.email}. {active.status === "trial" && `Free through ${new Date(active.trial_ends_at).toLocaleDateString()}, then $${active.price_month}/mo.`}
                                </p>
                            </div>
                            <GhostButton onClick={cancel} disabled={busy} data-testid="membership-cancel">
                                <X size={14} /> Cancel membership
                            </GhostButton>
                        </div>
                    )}

                    <div className="mt-8 grid gap-5 lg:grid-cols-3" data-testid="tier-grid">
                        {tiers.map((t, i) => (
                            <Reveal key={t.id} delay={i * 0.07}>
                                <TierCard tier={t} mine={active?.tier === t.id} onJoin={join} busy={busy} loggedIn={!!user} yearly={yearly} theme={theme} top={t.id === "silver"} />
                            </Reveal>
                        ))}
                    </div>

                    {user && !active && (
                        <p className="mt-6 text-sm text-zinc-500" data-testid="member-details">
                            Checkout is handled by Stripe — you won't be charged for 30 days, and you can cancel from your account page at any time.
                        </p>
                    )}

                    {!user && (
                        <div className="mt-8 flex flex-col items-start gap-4 rounded-3xl border border-zinc-200 bg-white p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="ws-login-prompt">
                            <div>
                                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">One account, everything</p>
                                <h3 className="mt-2 font-display text-2xl font-black tracking-tight text-zinc-900">Use the account you already have.</h3>
                                <p className="mt-1 text-sm text-zinc-600">Same login you order, sell and teach with.</p>
                            </div>
                            <div className="flex gap-3">
                                <Link to="/login"><StoreButton theme={theme} data-testid="ws-login">Log in</StoreButton></Link>
                                <Link to="/register"><GhostButton data-testid="ws-register">Create account</GhostButton></Link>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </Layout>
    );
}
